import { TrackEvent } from "./types";
import { getCookie, setCookie } from "./cookies";
import { captureAttribution, getAttribution } from "./attribution";
import { EventQueue } from "./queue";
import { sha256Hex } from "./identify";
import { hookPageView, hookUnload } from "./auto-capture";

const ANON_COOKIE = "_sx_id";
const ANON_TTL_DAYS = 730;

declare global { interface Window { statx?: any } }

function bootstrap() {
  const script = document.currentScript as HTMLScriptElement | null;
  const key = script?.dataset.key;
  const host = script?.dataset.host ?? new URL(script?.src ?? location.href).origin;
  const respectDnt = script?.dataset.respectDnt === "true";

  if (!key) {
    console.warn("[statx] missing data-key on script tag");
    return;
  }
  if (respectDnt && (navigator as any).doNotTrack === "1") {
    console.info("[statx] DNT enabled, tracker disabled");
    return;
  }

  // anon_id
  let anonId = getCookie(ANON_COOKIE) ?? localStorage.getItem(ANON_COOKIE);
  if (!anonId) {
    anonId = uuidv7();
    setCookie(ANON_COOKIE, anonId, ANON_TTL_DAYS);
    localStorage.setItem(ANON_COOKIE, anonId);
  } else if (!getCookie(ANON_COOKIE)) {
    setCookie(ANON_COOKIE, anonId, ANON_TTL_DAYS);
  }

  const { fbclid, gclid } = captureAttribution();

  const queue = new EventQueue({ host, key });

  function buildEvent(name: string, kind: "STANDARD" | "CUSTOM", props: Record<string, any> = {}): TrackEvent {
    const attr = getAttribution();
    return {
      event_id: uuidv7(),
      anon_id: anonId!,
      event_name: name,
      event_kind: kind,
      props,
      first_utm: attr.first_utm,
      last_utm: attr.last_utm,
      fbclid: fbclid,
      gclid: gclid,
      fbp: getCookie("_fbp") ?? undefined,
      fbc: getCookie("_fbc") ?? undefined,
      referrer: document.referrer || undefined,
      page_url: location.href,
      client_ts: new Date().toISOString(),
    };
  }

  // Public API
  window.statx = {
    track(name: string, props?: Record<string, any>) {
      queue.push(buildEvent(name, "CUSTOM", props ?? {}));
    },
    pageView() {
      queue.push(buildEvent("PageView", "STANDARD", { url: location.href, title: document.title }));
    },
    async identify(payload: { userId?: string; email?: string }) {
      const props: Record<string, any> = {};
      if (payload.userId) props.user_id = payload.userId;
      if (payload.email) props.email_hash = await sha256Hex(payload.email);
      queue.push(buildEvent("Identify", "STANDARD", props));
    },
    purchase(props: { value: number; currency: string; transactionId?: string; planId?: string }) {
      queue.push(buildEvent("Purchase", "STANDARD", props));
    },
    addPaymentInfo(props?: Record<string, any>) {
      queue.push(buildEvent("AddPaymentInfo", "STANDARD", props ?? {}));
    },
    reset() {
      // New anon_id, clear identity-bound state
      const newId = uuidv7();
      setCookie(ANON_COOKIE, newId, ANON_TTL_DAYS);
      localStorage.setItem(ANON_COOKIE, newId);
      anonId = newId;
    },
  };

  // Auto-capture
  hookPageView(() => window.statx.pageView());
  hookUnload(() => queue.flush(true));
}

function uuidv7(): string {
  // Simple UUIDv7: 48-bit timestamp ms + 74-bit random
  const ts = Date.now();
  const buf = new Uint8Array(16);
  buf[0] = (ts / 2 ** 40) & 0xff;
  buf[1] = (ts / 2 ** 32) & 0xff;
  buf[2] = (ts / 2 ** 24) & 0xff;
  buf[3] = (ts / 2 ** 16) & 0xff;
  buf[4] = (ts / 2 ** 8) & 0xff;
  buf[5] = ts & 0xff;
  crypto.getRandomValues(buf.subarray(6));
  buf[6] = (buf[6] & 0x0f) | 0x70; // version 7
  buf[8] = (buf[8] & 0x3f) | 0x80; // variant
  const hex = Array.from(buf, (b) => b.toString(16).padStart(2, "0")).join("");
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

bootstrap();
